/**
 * Password + email rules for the auth flow — the single source of truth.
 *
 * Before this module the rules lived inline in four components and disagreed:
 * the sign-up checklist accepted `length > 6` while `validatePassword` in the
 * same file demanded `>= 8`. Figma `66:47508` settles it at 8.
 */

export const PASSWORD_MIN_LENGTH = 8

export type PasswordRuleId = 'min8' | 'number' | 'upper' | 'lower' | 'special'

export interface PasswordRule {
  id: PasswordRuleId
  /** i18n key rendered on the requirement chip. */
  labelKey: string
  test: (value: string) => boolean
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'min8',
    labelKey: 'password_validation.min_8_chars',
    test: (value) => value.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: 'number',
    labelKey: 'password_validation.one_number',
    test: (value) => /\d/.test(value),
  },
  {
    id: 'upper',
    labelKey: 'password_validation.one_uppercase',
    test: (value) => /\p{Lu}/u.test(value),
  },
  {
    id: 'lower',
    labelKey: 'password_validation.one_lowercase',
    test: (value) => /\p{Ll}/u.test(value),
  },
  {
    id: 'special',
    labelKey: 'password_validation.one_special_char',
    test: (value) => /[!@#$%^&*(),.?":{}|<>_[\]\\/~`+=';-]/.test(value),
  },
] as const

export type PasswordRuleState = Record<PasswordRuleId, boolean>

export function evaluatePassword(value: string): PasswordRuleState {
  return PASSWORD_RULES.reduce((state, rule) => {
    state[rule.id] = rule.test(value)
    return state
  }, {} as PasswordRuleState)
}

export function isPasswordValid(value: string): boolean {
  return PASSWORD_RULES.every((rule) => rule.test(value))
}

/**
 * Deliberately permissive: the server is the authority on deliverability. This
 * only gates the submit button, so it must not reject addresses that are valid
 * but unusual.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function isEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim())
}
